import { useState } from 'react'

import { api } from '../api/client'
import { Card, EmptyState, ErrorBanner, Spinner, timeAgo } from '../components/common'
import { DataTable, type Column } from '../components/table'
import { TrafficArea, TrafficBars, formatBytes } from '../components/charts'
import { Endpoint, WindowPicker, useFlowWindow } from '../components/flows'
import type { Conversation } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Le conversazioni osservate, in tre livelli.
 *
 * Si arriva sempre nello stesso ordine: *chi parla di più* nella finestra
 * scelta, *com'è andato nel tempo*, *di cosa è fatto quel volume*. Aprire una
 * conversazione non cambia pagina — cambia il filtro dei due grafici sopra —
 * perché il confronto con il totale è metà della risposta.
 */
export default function Flows() {
  const [window, setWindow] = useFlowWindow()
  const [selected, setSelected] = useState<Conversation | null>(null)

  const conversations = useAsync(() => api.conversations({ window, limit: 500 }), [window])

  // I grafici seguono la selezione: senza selezione mostrano tutto il tenant.
  const filter = selected
    ? {
        window,
        src_addr: selected.src_addr,
        dst_addr: selected.dst_addr,
        port: selected.port,
      }
    : { window }
  const series = useAsync(() => api.flowSeries(filter), [window, selected])
  const breakdown = useAsync(
    () =>
      api.flowBreakdown({
        by: 'service',
        window,
        ...(selected?.src_ci_id ? { ci_id: selected.src_ci_id } : {}),
      }),
    [window, selected],
  )

  const total = conversations.data?.reduce((sum, c) => sum + c.bytes, 0) ?? 0

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Flussi di traffico rilevati')}</h1>
          <p className="page__subtitle">{t('Analizza le comunicazioni tra asset, evidenziando sorgenti, destinazioni, porte, protocolli e volumi di traffico.')}</p>
        </div>
        <WindowPicker value={window} onChange={setWindow} />
      </header>

      <ErrorBanner error={conversations.error} />

      {selected && (
        <div className="banner banner--info">
          {t('Filtrato su {sorgente} → {destinazione} ({servizio}/{protocollo}).', {
            sorgente: selected.src_label,
            destinazione: selected.dst_label,
            servizio: selected.service ?? selected.port,
            protocollo: selected.protocol,
          })}{' '}
          <button className="secondary" style={{ minHeight: 26 }} onClick={() => setSelected(null)}>
            {t('Mostra tutto il traffico')}
          </button>
        </div>
      )}

      <div className="row">
        <Card
          title={selected ? t('Andamento della conversazione') : t('Andamento del traffico complessivo')}
        >
          {series.loading && !series.data ? (
            <Spinner />
          ) : (
            <>
              <TrafficArea
                points={series.data?.points ?? []}
                stepSeconds={series.data?.step_seconds ?? 300}
              />
              {series.data && series.data.points.length > 0 && (
                <p className="field__hint" style={{ marginTop: 8 }}>
                  {t('{volume} in {n} intervalli da {risoluzione}.', {
                    volume: formatBytes(series.data.bytes_total),
                    n: series.data.points.length,
                    risoluzione: series.data.resolution,
                  })}
                </p>
              )}
            </>
          )}
        </Card>

        <Card title={t('Ripartizione per servizio')}>
          {breakdown.loading && !breakdown.data ? (
            <Spinner />
          ) : (
            <TrafficBars
              data={(breakdown.data ?? []).map((b) => ({
                key: b.key ?? b.label,
                label: b.label,
                bytes: b.bytes,
              }))}
            />
          )}
        </Card>
      </div>

      <Card
        title={t('Conversazioni ({n})', { n: conversations.data?.length ?? 0 })}
        actions={
          total > 0 ? <span className="field__hint">{t('{volume} in totale', { volume: formatBytes(total) })}</span> : null
        }
      >
        {conversations.loading && !conversations.data ? (
          <Spinner />
        ) : !conversations.data?.length ? (
          <EmptyState
            title={t('Nessun traffico osservato')}
            hint={t('Abilita la capability NetFlow su una sonda e configura gli apparati per esportare i flussi verso il suo indirizzo. La porta si sceglie nella scheda della sonda, sotto «Sorgenti dei flussi»: per difetto è la 2055.')}
          />
        ) : (
          <DataTable
            rows={conversations.data}
            columns={conversationColumns()}
            rowKey={(c) => `${c.src_addr}-${c.dst_addr}-${c.port}-${c.protocol_number}`}
            initialSort={{ key: 'bytes', desc: true }}
            onRowClick={setSelected}
            isSelected={(c) =>
              !!selected &&
              selected.src_addr === c.src_addr &&
              selected.dst_addr === c.dst_addr &&
              selected.port === c.port
            }
            sourceNote={t('Filtri e ordinamento agiscono sulle {n} conversazioni caricate per questa finestra, che il server sceglie per volume.', { n: conversations.data.length })}
          />
        )}
      </Card>
    </>
  )
}

/**
 * Le colonne delle conversazioni.
 *
 * `value` è ciò su cui si ordina e si filtra, `render` ciò che si vede. La
 * distinzione serve: il volume si mostra come «1,2 GB» e si ordina come byte,
 * e ordinare per la stringa metterebbe 900 MB dopo 1,2 GB — un elenco
 * plausibile e sbagliato, che è il modo peggiore di sbagliare.
 */
const conversationColumns = (): Column<Conversation>[] => [
  {
    key: 'source',
    header: t('Sorgente'),
    value: (c) => `${c.src_label} ${c.src_addr} ${c.src_ptr ?? ''} ${c.src_desc ?? ''}`,
    render: (c) => <Endpoint id={c.src_ci_id} label={c.src_label} addr={c.src_addr} ptr={c.src_ptr} desc={c.src_desc} />,
  },
  {
    key: 'destination',
    header: t('Destinazione'),
    value: (c) => `${c.dst_label} ${c.dst_addr} ${c.dst_ptr ?? ''} ${c.dst_desc ?? ''}`,
    render: (c) => <Endpoint id={c.dst_ci_id} label={c.dst_label} addr={c.dst_addr} ptr={c.dst_ptr} desc={c.dst_desc} />,
  },
  {
    key: 'service',
    header: t('Servizio'),
    value: (c) => c.service ?? String(c.port),
    render: (c) =>
      c.service ? (
        <>
          {c.service} <span className="text-muted">({c.port})</span>
        </>
      ) : (
        <span className="mono">{c.port}</span>
      ),
  },
  { key: 'port', header: t('Porta'), type: 'number', align: 'right', value: (c) => c.port },
  {
    key: 'protocol',
    header: t('Protocollo'),
    value: (c) => c.protocol,
    render: (c) => <span className="mono">{c.protocol}</span>,
  },
  {
    key: 'exporter',
    header: t('Exporter'),
    value: (c) => c.exporter ?? '',
    render: (c) =>
      c.exporter ? (
        <span className="mono">{c.exporter}</span>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
  {
    key: 'bytes',
    header: t('Volume'),
    type: 'number',
    align: 'right',
    value: (c) => c.bytes,
    render: (c) => formatBytes(c.bytes),
  },
  {
    key: 'flows',
    header: t('Flussi'),
    type: 'number',
    align: 'right',
    value: (c) => c.flows,
    render: (c) => c.flows.toLocaleString(localeCorrente()),
  },
  {
    key: 'last_seen',
    header: t('Ultima osservazione'),
    type: 'date',
    value: (c) => c.last_seen,
    render: (c) => <span title={c.last_seen ?? ''}>{timeAgo(c.last_seen)}</span>,
  },
]
